import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api, mutate } from '../shared/api';
import { SHELL_MODULE_REGISTRY, type ModuleKey } from '../shared/biModuleRegistry';
import { canOpenModule } from '../shared/biModuleKeys';
import { canOpenAdmin } from '../shared/foundationFilters';
import { moduleAvatar } from '../shared/moduleAvatars';
import type { User } from '../types/biApp';

type Locale = 'hr' | 'en';
type Task = { id: number; title: string; due_date: string | null; priority: string };
type MyTasks = { overdue: Task[]; today: Task[]; upcoming: Task[]; no_date: Task[] };

/** Avatar pločica (gradijent + ikona). */
function Avatar({ keyName, size = 56 }: { keyName: string; size?: number }) {
    const a = moduleAvatar(keyName);
    return (
        <div style={{
            width: size, height: size, borderRadius: size * 0.28, flex: 'none',
            background: `linear-gradient(135deg, ${a.grad[0]}, ${a.grad[1]})`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: size * 0.5, boxShadow: '0 4px 12px rgba(107,79,28,0.28)', color: '#fff',
        }}>{a.icon}</div>
    );
}

export function LandingModule({ user, token, locale, onOpenModule, onShortcutsChange }: { user: User; token: string; locale: Locale; onOpenModule: (key: ModuleKey) => void; onShortcutsChange?: (next: string[]) => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);

    // Svi dopušteni, vidljivi, top-level moduli (kao u rail-u) — auto uključuje nove.
    const modules = useMemo(() => SHELL_MODULE_REGISTRY.filter((e) => {
        if (e.key === 'dashboard' || e.parentKey || e.hiddenFromNav) return false;
        if (e.key === 'admin') return canOpenAdmin(user) && canOpenModule(user, 'admin');
        return canOpenModule(user, e.key);
    }).slice().sort((a, b) => a.code - b.code), [user]);

    const moduleByKey = useMemo(() => new Map(modules.map((m) => [m.key as string, m])), [modules]);
    const label = (key: string) => { const m = moduleByKey.get(key); return m ? (locale === 'en' ? m.labelEn : m.labelHr) : key; };

    const [shortcuts, setShortcuts] = useState<string[]>(() => (user.landing_shortcuts ?? []).filter((k) => true));
    const [addOpen, setAddOpen] = useState(false);
    const [tasks, setTasks] = useState<MyTasks | null>(null);

    useEffect(() => {
        // očisti shortcute na samo dopuštene/postojeće
        setShortcuts((prev) => prev.filter((k) => moduleByKey.has(k)));
    }, [moduleByKey]);

    useEffect(() => {
        void api<MyTasks>('/tasks/my-tasks', token).then(setTasks).catch(() => setTasks(null));
    }, [token]);

    const persist = useCallback((next: string[]) => {
        setShortcuts(next);
        // Osvježi i parent user state — inače remount (navigacija dashboard→modul→dashboard)
        // čita stari user.landing_shortcuts i prečaci "nestanu" dok se ne napravi full reload.
        onShortcutsChange?.(next);
        void mutate('/landing/shortcuts', token, 'PUT', { shortcuts: next }).catch(() => undefined);
    }, [token, onShortcutsChange]);

    const addShortcut = (key: string) => { if (shortcuts.includes(key) || shortcuts.length >= 10) return; persist([...shortcuts, key]); setAddOpen(false); };
    const removeShortcut = (key: string) => persist(shortcuts.filter((k) => k !== key));
    const move = (i: number, dir: -1 | 1) => {
        const j = i + dir; if (j < 0 || j >= shortcuts.length) return;
        const n = shortcuts.slice(); [n[i], n[j]] = [n[j], n[i]]; persist(n);
    };
    const addable = modules.filter((m) => !shortcuts.includes(m.key as string));

    const overdue = tasks?.overdue ?? [];
    const upcoming = [...(tasks?.today ?? []), ...(tasks?.upcoming ?? [])].slice(0, 8);
    const fmtDue = (d: string | null) => d ? new Date(d.replace(' ', 'T')).toLocaleDateString('hr-HR', { day: '2-digit', month: '2-digit' }) : '';

    const sectionTitle: React.CSSProperties = { color: 'var(--ad-ink)', fontSize: '1.05rem', fontWeight: 800, margin: '0 0 12px', letterSpacing: '0.01em' };

    return (
        <section style={{ maxWidth: 1200, margin: '0 auto' }}>
            {/* ───────── Shortcuti ───────── */}
            <div style={{ marginBottom: 28 }}>
                <h2 style={sectionTitle}>⭐ {t('Moji prečaci', 'My shortcuts')} <span className="muted" style={{ fontWeight: 400, fontSize: '0.8rem' }}>({shortcuts.length}/10)</span></h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                    {shortcuts.map((key, i) => (
                        <div key={key} className="land-shortcut" style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 12, padding: 12, borderRadius: 14, border: '1px solid rgba(var(--ad-soft-rgb),0.35)', background: '#fffdf6', cursor: 'pointer', boxShadow: '0 2px 8px rgba(107,79,28,0.08)' }}
                            onClick={() => onOpenModule(key as ModuleKey)}>
                            <Avatar keyName={key} size={48} />
                            <strong style={{ flex: 1, color: '#2a2417', fontSize: '0.95rem' }}>{label(key)}</strong>
                            <div className="land-actions" style={{ display: 'flex', gap: 2 }} onClick={(e) => e.stopPropagation()}>
                                <button type="button" title={t('Lijevo', 'Left')} disabled={i === 0} onClick={() => move(i, -1)} style={ctrlBtn}>◀</button>
                                <button type="button" title={t('Desno', 'Right')} disabled={i === shortcuts.length - 1} onClick={() => move(i, 1)} style={ctrlBtn}>▶</button>
                                <button type="button" title={t('Ukloni', 'Remove')} onClick={() => removeShortcut(key)} style={{ ...ctrlBtn, color: '#b14040' }}>✕</button>
                            </div>
                        </div>
                    ))}
                    {shortcuts.length < 10 ? (
                        <div style={{ position: 'relative' }}>
                            <button type="button" onClick={() => setAddOpen((o) => !o)}
                                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 12, minHeight: 72, borderRadius: 14, border: '2px dashed rgba(var(--ad-soft-rgb),0.5)', background: 'rgba(var(--ad-acc-rgb),0.06)', cursor: 'pointer', color: 'var(--ad-acc-3)', fontWeight: 700 }}>
                                ＋ {t('Dodaj prečac', 'Add shortcut')}
                            </button>
                            {addOpen ? (
                                <div style={{ position: 'absolute', zIndex: 30, top: '100%', left: 0, right: 0, marginTop: 4, maxHeight: 320, overflowY: 'auto', background: '#fffdf7', border: '1px solid rgba(var(--ad-soft-rgb),0.5)', borderRadius: 10, boxShadow: '0 10px 30px rgba(0,0,0,0.18)' }}>
                                    {addable.length === 0 ? <div className="muted" style={{ padding: 12, fontSize: '0.85rem' }}>{t('Svi su dodani.', 'All added.')}</div> : addable.map((m) => (
                                        <button key={m.key} type="button" onClick={() => addShortcut(m.key as string)}
                                            style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '7px 10px', border: 0, borderBottom: '1px solid rgba(var(--ad-soft-rgb),0.15)', background: 'transparent', cursor: 'pointer' }}>
                                            <Avatar keyName={m.key as string} size={30} />
                                            <span style={{ fontSize: '0.88rem', color: '#2a2417' }}>{locale === 'en' ? m.labelEn : m.labelHr}</span>
                                        </button>
                                    ))}
                                </div>
                            ) : null}
                        </div>
                    ) : null}
                </div>
                {shortcuts.length === 0 ? <p className="muted" style={{ fontSize: '0.84rem', marginTop: 8, fontStyle: 'italic' }}>{t('Dodaj module koje najviše koristiš — klikni „＋ Dodaj prečac".', 'Add the modules you use most — click “＋ Add shortcut”.')}</p> : null}
            </div>

            {/* ───────── Taskovi ───────── */}
            {(overdue.length > 0 || upcoming.length > 0) ? (
                <div style={{ marginBottom: 28 }}>
                    <h2 style={sectionTitle}>🎯 {t('Neposredni zadaci', 'Immediate tasks')}{overdue.length > 0 ? <span style={{ marginLeft: 8, background: '#b14040', color: '#fff', borderRadius: 10, padding: '1px 8px', fontSize: '0.72rem', fontWeight: 800 }}>{overdue.length} {t('probijeno', 'overdue')}</span> : null}</h2>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10 }}>
                        {overdue.map((tk) => (
                            <div key={'o' + tk.id} onClick={() => onOpenModule('team_tasks')} style={{ ...taskCard, borderLeft: '4px solid #b14040', background: 'rgba(177,64,64,0.06)' }}>
                                <div style={{ fontWeight: 700, color: '#2a2417', fontSize: '0.9rem' }}>{tk.title}</div>
                                <div style={{ fontSize: '0.76rem', color: '#b14040', fontWeight: 700 }}>⚠ {t('kasni', 'overdue')} · {fmtDue(tk.due_date)}</div>
                            </div>
                        ))}
                        {upcoming.map((tk) => (
                            <div key={'u' + tk.id} onClick={() => onOpenModule('team_tasks')} style={{ ...taskCard, borderLeft: '4px solid var(--ad-acc)' }}>
                                <div style={{ fontWeight: 700, color: '#2a2417', fontSize: '0.9rem' }}>{tk.title}</div>
                                <div style={{ fontSize: '0.76rem', color: 'var(--ad-acc-3)' }}>📅 {fmtDue(tk.due_date) || t('uskoro', 'soon')}</div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}

            {/* ───────── Svi moduli ───────── */}
            <div>
                <h2 style={sectionTitle}>🧭 {t('Svi moduli', 'All modules')}</h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 14 }}>
                    {modules.map((m) => (
                        <button key={m.key} type="button" onClick={() => onOpenModule(m.key as ModuleKey)}
                            className="land-card"
                            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 8, padding: '16px 10px', borderRadius: 16, border: '1px solid rgba(var(--ad-soft-rgb),0.3)', background: '#fffdf6', cursor: 'pointer', boxShadow: '0 2px 8px rgba(107,79,28,0.08)', transition: 'transform 0.12s, box-shadow 0.12s' }}>
                            <Avatar keyName={m.key as string} size={56} />
                            <strong style={{ color: '#2a2417', fontSize: '0.92rem' }}>{locale === 'en' ? m.labelEn : m.labelHr}</strong>
                            <span className="muted" style={{ fontSize: '0.72rem', lineHeight: 1.35, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{locale === 'en' ? m.leadEn : m.leadHr}</span>
                        </button>
                    ))}
                </div>
            </div>

            <style>{`
                .land-shortcut .land-actions { opacity: 0; transition: opacity 0.12s; }
                .land-shortcut:hover .land-actions { opacity: 1; }
                .land-card:hover { transform: translateY(-3px); box-shadow: 0 8px 22px rgba(107,79,28,0.18); }
                @media (max-width: 1000px) { section > div > div[style*="repeat(5"] { grid-template-columns: repeat(3, 1fr) !important; } section > div > div[style*="repeat(4"] { grid-template-columns: repeat(2, 1fr) !important; } }
                @media (max-width: 560px) { section > div > div[style*="repeat(5"], section > div > div[style*="repeat(4"] { grid-template-columns: repeat(2, 1fr) !important; } }
            `}</style>
        </section>
    );
}

const ctrlBtn: React.CSSProperties = { border: 0, background: 'rgba(var(--ad-soft-rgb),0.14)', borderRadius: 6, width: 22, height: 22, cursor: 'pointer', fontSize: '0.7rem', color: 'var(--ad-ink)', lineHeight: 1, padding: 0 };
const taskCard: React.CSSProperties = { padding: '10px 12px', borderRadius: 12, border: '1px solid rgba(var(--ad-soft-rgb),0.3)', background: '#fffdf6', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 4 };
